/*
 * Hiza — bundle builder
 *
 * Drives snippets/hiza-bundle-builder.liquid: the product being viewed is
 * piece one, the shopper adds one or two DIFFERENT products from the rail, and
 * the whole lot goes to the cart in a single /cart/add.js call.
 *
 * Why a separate button instead of driving the product form, which is what the
 * first version did: a product form posts one product. Several different
 * products can only be added together through the AJAX cart API. Dawn's own
 * Add to cart is left exactly as it was for buying this piece on its own.
 *
 * Why it then goes to the cart rather than popping Dawn's cart notification:
 * the notification renders ONE line item (it keys off `parsedState.key`), and
 * more importantly the discount only becomes visible in the cart totals. The
 * cart is where the shopper sees the 20% or 30% actually come off, which is
 * the whole point of having built a bundle.
 *
 * No price maths anywhere in here. Percentages are copy from the block
 * settings; every number is Shopify's.
 */
(function () {
  'use strict';

  var TIER_2 = 2;
  var TIER_3 = 3;

  function scopeOf(el) {
    return el.closest('product-info, .product, .shopify-section') || document;
  }

  function wire(root) {
    if (root.dataset.hzBbReady === 'true') return;

    var scope = scopeOf(root);
    var form = scope.querySelector('form[action*="/cart/add"]');
    var variantInput = form && form.querySelector('input[name="id"]');
    var formSubmit = scope.querySelector('.product-form__submit');
    if (!variantInput) return;

    root.dataset.hzBbReady = 'true';

    var items = Array.prototype.slice.call(root.querySelectorAll('[data-hz-bb-item]'));
    var submit = root.querySelector('[data-hz-bb-submit]');
    var label = root.querySelector('[data-hz-bb-label]');
    var status = root.querySelector('[data-hz-bb-status]');
    var note2 = root.dataset.hzTier2Note || '20% off';
    var note3 = root.dataset.hzTier3Note || '30% off';
    var busy = false;

    function partOf(item) {
      return {
        check: item.querySelector('[data-hz-bb-check]'),
        variant: item.querySelector('[data-hz-bb-variant]'),
        name: item.dataset.hzBbName || 'this piece'
      };
    }

    function chosen() {
      return items.map(partOf).filter(function (p) {
        return p.check && p.check.checked;
      });
    }

    function render() {
      if (busy) return;

      var picked = chosen();
      var missing = picked.filter(function (p) {
        return !p.variant || !p.variant.value;
      });
      var total = picked.length + 1; // + the piece being viewed

      items.forEach(function (item) {
        var p = partOf(item);
        item.classList.toggle('is-selected', !!(p.check && p.check.checked));
      });

      // Mirror Dawn's own Add to cart. When the size chosen above is sold out
      // Dawn disables its button but leaves the id input holding the last
      // value, so the id alone is not enough to know the piece is buyable.
      var currentReady = !!variantInput.value && !(formSubmit && formSubmit.disabled);

      status.classList.remove('hz-bb__status--error');

      if (!currentReady) {
        status.textContent = 'Choose an available size for this piece first.';
        submit.disabled = true;
      } else if (total < TIER_2) {
        status.textContent = 'Add one more piece for ' + note2 + '.';
        submit.disabled = true;
      } else if (missing.length) {
        status.textContent = 'Choose a size for ' + missing[0].name + '.';
        submit.disabled = true;
      } else if (total === TIER_2) {
        status.textContent = note2 + ' — add one more for ' + note3 + '.';
        submit.disabled = false;
      } else {
        status.textContent = note3 + ' — the best rate.';
        submit.disabled = false;
      }

      label.textContent =
        total < TIER_2
          ? 'Add bundle to cart'
          : 'Add ' + total + ' pieces to cart · ' + (total >= TIER_3 ? note3 : note2);
    }

    items.forEach(function (item) {
      var p = partOf(item);
      if (!p.check) return;

      p.check.addEventListener('change', function () {
        // Picking a piece with several sizes is not a choice until the size is
        // chosen too, so send them straight there.
        if (p.check.checked && p.variant && p.variant.tagName === 'SELECT' && !p.variant.value) {
          p.variant.focus();
        }
        render();
      });

      if (p.variant && p.variant.tagName === 'SELECT') {
        p.variant.addEventListener('change', function () {
          // Choosing a size is the clearest statement of intent there is —
          // treat it as selecting the piece, and clearing it as dropping it.
          p.check.checked = !!p.variant.value;
          render();
        });
      }
    });

    // Dawn rewrites the form's variant id when the shopper changes size above,
    // and dispatches a bubbling change on it when it does.
    variantInput.addEventListener('change', render);
    form.addEventListener('change', render);

    // Its sold-out toggle lands on the button a tick later, after the section
    // fetch resolves, so watch the attribute rather than guess at the timing.
    if (formSubmit && window.MutationObserver) {
      new MutationObserver(render).observe(formSubmit, {
        attributes: true,
        attributeFilter: ['disabled']
      });
    }

    submit.addEventListener('click', function () {
      if (busy || submit.disabled) return;

      var payload = [{ id: Number(variantInput.value), quantity: 1 }];
      chosen().forEach(function (p) {
        payload.push({ id: Number(p.variant.value), quantity: 1 });
      });
      if (payload.length < TIER_2) return;

      busy = true;
      submit.disabled = true;
      root.classList.add('is-busy');
      var restore = label.textContent;
      label.textContent = 'Adding…';
      status.textContent = '';

      var url = (window.routes && window.routes.cart_add_url) || '/cart/add';

      fetch(url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: payload })
      })
        .then(function (response) {
          return response.json().then(function (data) {
            return { ok: response.ok, data: data };
          });
        })
        .then(function (result) {
          if (!result.ok) throw new Error(result.data.description || result.data.message || '');
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        })
        .catch(function (error) {
          busy = false;
          root.classList.remove('is-busy');
          label.textContent = restore;
          render(); // put the tiles and the button back before saying what failed
          status.textContent =
            error.message || 'That could not be added to the cart. Please try again.';
          status.classList.add('hz-bb__status--error');
        });
    });

    render();
  }

  function init() {
    document.querySelectorAll('[data-hz-bundle-builder]').forEach(wire);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-render in the theme editor, which replaces a section wholesale.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);
})();
